import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import {
  ThemeRoute,
  ThemeRouteLanguageCode,
  ThemeRouteCustomWaypoint,
  ThemeRoutePoiRef,
  ThemeRouteStatus,
  ThemeRouteThemeKey,
  ThemeRouteTranslations,
} from '@/types/theme-routes.types';
import baseApi from './RootBaseApi';

interface ThemeRouteApiPoi {
  poi_id: string | null;
  harbor_id?: string | null;
  order: number;
  poi?: {
    id: string;
    name?: string;
    description?: string;
    latitude?: string | number | null;
    longitude?: string | number | null;
    images?: string[];
  };
  harbor?: {
    id: string;
    name?: string;
    latitude?: string | number | null;
    longitude?: string | number | null;
  };
}

interface ThemeRouteApiModel {
  id: string;
  name?: string;
  description?: string;
  theme: ThemeRouteThemeKey;
  type?: 'themed' | 'harbor' | 1 | 2 | '1' | '2';
  status: number;
  language: ThemeRouteLanguageCode;
  translations: ThemeRouteTranslations;
  is_locked?: boolean;
  pois?: ThemeRouteApiPoi[];
  custom_waypoints?: ThemeRouteCustomWaypoint[];
  media?: string[];
  image_captions?: {
    image_index: number;
    captions: Record<string, string>;
  };
  created_at?: string;
  updated_at?: string;
}

const isThemeRouteCustomWaypoint = (
  value: ThemeRouteCustomWaypoint | null
): value is ThemeRouteCustomWaypoint => value !== null;

const toNumber = (value: string | number | null | undefined) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const mapApiRouteToThemeRoute = (api: ThemeRouteApiModel): ThemeRoute => {
  const defaultLanguage = (api.language || 'en') as ThemeRouteLanguageCode;
  const translations = (api.translations || {
    en: {},
    de: {},
    fi: {},
    sv: {},
  }) as ThemeRouteTranslations;

  const normalizedType =
    api.type === 1
      ? ('harbor' as const)
      : api.type === 2
        ? ('themed' as const)
        : api.type === '1'
          ? ('harbor' as const)
          : api.type === '2'
            ? ('themed' as const)
            : api.type;

  const customWaypoints = Array.isArray(api.custom_waypoints)
    ? api.custom_waypoints
        .map((waypoint): ThemeRouteCustomWaypoint | null => {
          if (!waypoint) return null;
          if (
            typeof waypoint.lat !== 'number' ||
            typeof waypoint.lon !== 'number' ||
            typeof waypoint.label !== 'string'
          ) {
            return null;
          }

          const waypointTranslationsRaw = (
            waypoint as unknown as {
              translations?: unknown;
            }
          ).translations;

          const waypointTranslations:
            | Partial<Record<ThemeRouteLanguageCode, { description?: string }>>
            | undefined =
            waypointTranslationsRaw &&
            typeof waypointTranslationsRaw === 'object' &&
            !Array.isArray(waypointTranslationsRaw)
              ? (waypointTranslationsRaw as Partial<
                  Record<ThemeRouteLanguageCode, { description?: string }>
                >)
              : undefined;

          return {
            ...(waypoint.id ? { id: waypoint.id } : {}),
            lat: waypoint.lat,
            lon: waypoint.lon,
            label: waypoint.label,
            description:
              typeof waypoint.description === 'string'
                ? waypoint.description
                : undefined,
            translations: waypointTranslations,
          };
        })
        .filter(isThemeRouteCustomWaypoint)
    : undefined;

  const poiSequence: ThemeRoutePoiRef[] = Array.isArray(api.pois)
    ? api.pois.map((item) => {
        const poi = item.poi;
        const harbor = item.harbor;

        const primaryImage =
          poi && Array.isArray(poi.images) && poi.images.length > 0
            ? poi.images[0]
            : undefined;

        return {
          poiId: item.poi_id ?? null,
          harborId: item.harbor_id ?? undefined,
          poiName: poi?.name,
          harborName: harbor?.name,
          latitude: toNumber(poi?.latitude),
          longitude: toNumber(poi?.longitude),
          description: poi?.description,
          imageUrl: primaryImage,
          harborLatitude: toNumber(harbor?.latitude),
          harborLongitude: toNumber(harbor?.longitude),
        };
      })
    : [];

  let status: ThemeRouteStatus;
  switch (api.status) {
    case 0:
      status = 'draft';
      break;
    case 1:
      status = 'published';
      break;
    case 2:
      status = 'pending_approval';
      break;
    case 3:
      status = 'approved';
      break;
    case 4:
      status = 'rejected';
      break;
    default:
      status = 'draft';
      break;
  }

  const baseTranslation = translations[defaultLanguage] || {};

  const media = Array.isArray(api.media)
    ? api.media.filter((item): item is string => typeof item === 'string')
    : undefined;

  return {
    id: api.id,
    type: normalizedType,
    defaultLanguage,
    name: api.name || baseTranslation.name || '',
    description:
      typeof api.description === 'string'
        ? api.description
        : baseTranslation.description,
    translations,
    themeKey: api.theme,
    status,
    isLocked: api.is_locked === true,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    poiIds: poiSequence
      .map((p) => p.poiId)
      .filter((id): id is string => typeof id === 'string' && id.trim() !== ''),
    poiSequence,
    customWaypoints,
    media,
    image_captions: api.image_captions,
  };
};

export interface ThemeRouteListParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: 1 | 2 | '1' | '2';
  harbor_id?: string;
}

export const ThemeRoutesService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getThemeRoutes: builder.query<
      PaginatedApiResponse<ThemeRoute[]>,
      ThemeRouteListParams | void
    >({
      query: (params) => ({
        url: '/admin/theme-routes',
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: PaginatedApiResponse<ThemeRouteApiModel[]>
      ): PaginatedApiResponse<ThemeRoute[]> => ({
        ...response,
        data: Array.isArray(response.data)
          ? response.data.map(mapApiRouteToThemeRoute)
          : [],
      }),
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map((item) => ({
                type: 'ThemeRoutes' as const,
                id: item.id,
              })),
              { type: 'ThemeRoutes' as const, id: 'LIST' },
            ]
          : [{ type: 'ThemeRoutes' as const, id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),
    getHarborThemeRoutes: builder.query<
      PaginatedApiResponse<ThemeRoute[]>,
      ThemeRouteListParams | void
    >({
      query: (params) => ({
        url: '/admin/theme-routes/harbor',
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: PaginatedApiResponse<ThemeRouteApiModel[]>
      ): PaginatedApiResponse<ThemeRoute[]> => ({
        ...response,
        data: Array.isArray(response.data)
          ? response.data.map(mapApiRouteToThemeRoute)
          : [],
      }),
      providesTags: (result) =>
        result?.data && Array.isArray(result.data)
          ? [
              ...result.data.map((item) => ({
                type: 'ThemeRoutes' as const,
                id: item.id,
              })),
              { type: 'ThemeRoutes' as const, id: 'LIST' },
            ]
          : [{ type: 'ThemeRoutes' as const, id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),
    getThemeRouteById: builder.query<ThemeRoute, string>({
      query: (id) => ({
        url: `/admin/theme-routes/${id}`,
        method: 'GET',
      }),
      transformResponse: (
        response: ApiResponse<ThemeRouteApiModel> | ThemeRouteApiModel
      ): ThemeRoute => {
        const raw =
          (response as ApiResponse<ThemeRouteApiModel>)?.data ??
          (response as ThemeRouteApiModel);
        return mapApiRouteToThemeRoute(raw);
      },
      providesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id },
      ],
      keepUnusedDataFor: 0,
    }),

    createThemeRoute: builder.mutation<
      ApiResponse<ThemeRoute> | ThemeRoute,
      FormData
    >({
      query: (data) => ({
        url: '/admin/theme-routes',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'ThemeRoutes', id: 'LIST' }],
    }),

    updateThemeRoute: builder.mutation<
      ApiResponse<ThemeRoute> | ThemeRoute,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `/admin/theme-routes/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),

    deleteThemeRoute: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/admin/theme-routes/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),

    translateThemeRoute: builder.mutation<
      { translations: ThemeRouteTranslations },
      {
        name?: string;
        description?: string;
        source_language?: string;
        theme_route_id?: string;
      }
    >({
      query: (data) => ({
        url: '/admin/theme-routes/translate',
        method: 'POST',
        data,
      }),
      transformResponse: (response: { translations: ThemeRouteTranslations }) =>
        response,
    }),

    // Request approval for a specific theme route
    requestThemeRouteApproval: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: `/admin/theme-routes/${id}/request-approval`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),

    // Get all pending approval requests
    getPendingThemeRouteApprovals: builder.query<
      PaginatedApiResponse<unknown[]>,
      void
    >({
      query: () => ({
        url: '/admin/theme-routes/approvals/pending',
        method: 'GET',
      }),
      // Use a generic unknown[] payload as the exact shape is backend-defined
      transformResponse: (
        response: PaginatedApiResponse<unknown[]>
      ): PaginatedApiResponse<unknown[]> => response,
      providesTags: [{ type: 'ThemeRoutes' as const, id: 'APPROVALS' }],
      keepUnusedDataFor: 0,
    }),

    // Review an approval request (approve or reject)
    reviewThemeRouteApproval: builder.mutation<
      ApiResponse<unknown>,
      {
        approvalId: string;
        status: number;
        notes?: string;
        rejection_reason?: string;
      }
    >({
      query: ({ approvalId, status, notes, rejection_reason }) => ({
        url: `/admin/theme-routes/approvals/${approvalId}/review`,
        method: 'PUT',
        data: {
          status,
          notes,
          rejection_reason,
        },
      }),
      invalidatesTags: [{ type: 'ThemeRoutes' as const, id: 'APPROVALS' }],
    }),

    // Publish an approved theme route
    publishThemeRoute: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: `/admin/theme-routes/${id}/publish`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),

    // Get revision history for a given theme route
    getThemeRouteRevisions: builder.query<
      PaginatedApiResponse<unknown[]>,
      string
    >({
      query: (id) => ({
        url: `/admin/theme-routes/${id}/revisions`,
        method: 'GET',
      }),
      transformResponse: (
        response: PaginatedApiResponse<unknown[]>
      ): PaginatedApiResponse<unknown[]> => response,
      providesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id: `${id}-revisions` },
      ],
      keepUnusedDataFor: 0,
    }),

    // Get a specific revision by its ID
    getThemeRouteRevisionById: builder.query<ApiResponse<unknown>, string>({
      query: (revisionId) => ({
        url: `/admin/theme-routes/revisions/${revisionId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<unknown>) => response,
      keepUnusedDataFor: 0,
    }),

    // Revert a theme route to a specific revision version
    revertThemeRouteToRevision: builder.mutation<
      ApiResponse<unknown>,
      { id: string; version: number; reason?: string }
    >({
      query: ({ id, version, reason }) => ({
        url: `/admin/theme-routes/${id}/revert`,
        method: 'POST',
        data: {
          version,
          reason,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
        { type: 'ThemeRoutes' as const, id: `${id}-revisions` },
      ],
    }),

    // Get comments for a specific theme route
    getThemeRouteComments: builder.query<
      PaginatedApiResponse<unknown[]>,
      string
    >({
      query: (id) => ({
        url: `/admin/theme-routes/${id}/comments`,
        method: 'GET',
      }),
      transformResponse: (
        response: PaginatedApiResponse<unknown[]>
      ): PaginatedApiResponse<unknown[]> => response,
      providesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id: `${id}-comments` },
      ],
      keepUnusedDataFor: 0,
    }),

    // Add a comment to a specific theme route
    addThemeRouteComment: builder.mutation<
      ApiResponse<unknown>,
      { id: string; comment: string; parent_comment_id?: string }
    >({
      query: ({ id, comment, parent_comment_id }) => ({
        url: `/admin/theme-routes/${id}/comments`,
        method: 'POST',
        data: {
          comment,
          parent_comment_id,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),

    // Unlock a locked theme route so it can be edited
    unlockThemeRoute: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: `/admin/theme-routes/${id}/unlock`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ThemeRoutes' as const, id },
        { type: 'ThemeRoutes' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetThemeRoutesQuery,
  useGetHarborThemeRoutesQuery,
  useGetThemeRouteByIdQuery,
  useCreateThemeRouteMutation,
  useUpdateThemeRouteMutation,
  useDeleteThemeRouteMutation,
  useTranslateThemeRouteMutation,
  useRequestThemeRouteApprovalMutation,
  useGetPendingThemeRouteApprovalsQuery,
  useReviewThemeRouteApprovalMutation,
  usePublishThemeRouteMutation,
  useGetThemeRouteRevisionsQuery,
  useGetThemeRouteRevisionByIdQuery,
  useRevertThemeRouteToRevisionMutation,
  useGetThemeRouteCommentsQuery,
  useAddThemeRouteCommentMutation,
  useUnlockThemeRouteMutation,
} = ThemeRoutesService;
